import type { DashboardBucket, StarlinkDashboardPayload } from '@/components/value-chain/starlinkDashboard';
import { bucketById } from '@/components/value-chain/starlinkDashboard';
import type { StarlinkMarket, StarlinkMarketsPayload } from '@/components/value-chain/starlinkMarkets';
import { STARLINK_HISTORY } from '@/components/value-chain/starlinkProduct';

export const OVERVIEW_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'constellation', label: 'Constellation' },
  { id: 'launches', label: 'Launches' },
  { id: 'demand', label: 'Demand' },
  { id: 'geography', label: 'Geography' },
  { id: 'government', label: 'Government' },
  { id: 'spectrum', label: 'Spectrum' },
  { id: 'performance', label: 'Performance' },
  { id: 'competition', label: 'Competition' },
  { id: 'financials', label: 'Financials' },
] as const;

export type OverviewSectionId = (typeof OVERVIEW_SECTIONS)[number]['id'];

export type KitGeoRow = {
  country: string;
  units: number | null;
  lines: number | null;
};

export function bucketStat(payload: StarlinkDashboardPayload, id: string, label: string): string {
  const bucket = bucketById(payload, id);
  const row = bucket?.stats.find((stat) => stat.label === label);
  return row?.value ?? '—';
}

export function parseKitRows(bucket: DashboardBucket | undefined): KitGeoRow[] {
  return (bucket?.rows ?? []).map((row) => {
    const unitsMatch = row.value.match(/([\d,]+)\s+units/i);
    const linesMatch = row.value.match(/([\d,]+)\s+lines/i);
    const units = unitsMatch ? Number(unitsMatch[1].replace(/,/g, '')) : null;
    const lines = linesMatch ? Number(linesMatch[1].replace(/,/g, '')) : null;
    return {
      country: row.label,
      units: units && units > 0 ? units : null,
      lines: lines && lines > 0 ? lines : null,
    };
  });
}

export function formatUpdated(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Pacific/Honolulu',
    timeZoneName: 'short',
  });
}

export function sparkFromHistory(key: 'consumerM' | 'enterpriseM' | 'subscribersM'): number[] {
  return STARLINK_HISTORY.map((row) => row[key]).filter((value): value is number => value != null);
}

export function qosCurve(markets: StarlinkMarket[]): number[] {
  const values = markets
    .map((market) => market.download_p50)
    .filter((value): value is number => value != null)
    .sort((a, b) => a - b);
  if (values.length < 8) return values;
  const step = Math.max(1, Math.floor(values.length / 24));
  return values.filter((_, i) => i % step === 0);
}

export function liveCount(payload: StarlinkMarketsPayload): number {
  return payload.kpis.live;
}

export const LAUNCH_SERIES = [
  { year: '2020', launches: 26, printed: false },
  { year: '2021', launches: 31, printed: false },
  { year: '2022', launches: 61, printed: false },
  { year: '2023', launches: 96, printed: false },
  { year: '2024', launches: 134, printed: false },
  { year: '2025', launches: 170, printed: true },
  { year: '2026', launches: 40, printed: true },
];

export const KIT_COST_INDEX = [
  { year: '2022', index: 100 },
  { year: '2023', index: 72 },
  { year: '2024', index: 54 },
  { year: '2025', index: 41 },
];
